import React from 'react';
import { Box, Typography, Link, Container, useTheme } from '@mui/material';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';

interface Citation {
  title: string;
  authors: string;
  journal?: string;
  year: string;
  url: string;
  doi?: string;
}

interface CitationsProps {
  citationNumbers?: string[];
}

const contentUseCase = new ContentUseCase();

export const Citations: React.FC<CitationsProps> = ({ citationNumbers }) => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getAllContent(language);
  const citations = content.citations as Record<string, Citation>;

  if (!citations || Object.keys(citations).length === 0) {
    return null;
  }

  // If no specific citations provided, show all
  const citationsToShow = citationNumbers
    ? Object.entries(citations).filter(([number]) => citationNumbers.includes(number))
    : Object.entries(citations);

  if (citationsToShow.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        py: theme.spacing(1),
        px: theme.spacing(2),
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="caption"
          component="h3"
          sx={{
            mb: theme.spacing(1),
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary'
          }}
        >
          {language === 'de' ? 'Quellen' : 'References'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
          {citationsToShow.map(([number, citation]) => (
            <Box key={number} sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
              <Typography
                variant="caption"
                component="span"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  minWidth: '15px',
                  fontSize: '0.7rem'
                }}
              >
                [{number}]
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" component="div" sx={{ mb: 0.1, fontSize: '0.7rem' }}>
                  <strong>{citation.authors}</strong>
                  {citation.title && (
                    <>
                      . {citation.title}
                      {citation.journal && (
                        <em>. {citation.journal}</em>
                      )}
                      {citation.year && `, ${citation.year}`}.
                    </>
                  )}
                </Typography>
                {citation.url && (
                  <Link
                    href={citation.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontSize: '0.65rem',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {citation.url}
                  </Link>
                )}
                {citation.doi && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.1, fontSize: '0.65rem' }}>
                    DOI: {citation.doi}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

